import React from 'react';
import { Alert, Button, Card, Descriptions, Space, Tag, Typography } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { formatMoney } from '@/utils/format';

const { Text } = Typography;

export type EstimateSourceInfo = {
  currentLabel: string;
  currentRevision: number;
  latestLabel?: string | null;
  latestRevision?: number | null;
  latestApprovedId?: string | null;
  costDelta?: number | null;
  reviewStatus: 'NOT_REVIEWED' | 'IN_REVIEW' | 'APPLIED' | 'STALE';
};

type Props = {
  info: EstimateSourceInfo;
  onCompare?: () => void;
  disabled?: boolean;
};

const statusTag: Record<EstimateSourceInfo['reviewStatus'], { color: string; label: string }> = {
  NOT_REVIEWED: { color: 'orange', label: 'Changes not reviewed' },
  IN_REVIEW: { color: 'blue', label: 'Reconciliation in progress' },
  APPLIED: { color: 'green', label: 'Estimate revision applied' },
  STALE: { color: 'red', label: 'Reconciliation stale' },
};

export function QuotationEstimateSourceCard({ info, onCompare, disabled }: Props) {
  const st = statusTag[info.reviewStatus];
  const showLatest =
    info.latestRevision != null &&
    info.latestRevision > info.currentRevision &&
    info.latestApprovedId;

  return (
    <Card size="small" title="Estimate source">
      <Descriptions column={1} size="small">
        <Descriptions.Item label="Current">{info.currentLabel}</Descriptions.Item>
        {showLatest ? (
          <Descriptions.Item label="Latest approved">
            {info.latestLabel ?? `REV ${info.latestRevision}`}
          </Descriptions.Item>
        ) : null}
        {info.costDelta != null && showLatest ? (
          <Descriptions.Item label="Cost delta">
            <Text type={info.costDelta >= 0 ? 'danger' : 'success'}>
              {info.costDelta >= 0 ? '+' : ''}
              {formatMoney(info.costDelta)}
            </Text>
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label="Status">
          <Tag color={st.color}>{st.label}</Tag>
        </Descriptions.Item>
      </Descriptions>
      {showLatest ? (
        <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
          <Alert
            type="info"
            showIcon
            message="New estimate revision available"
            description="Review changes before applying to this draft quotation."
          />
          <Button
            type="primary"
            icon={<SwapOutlined />}
            onClick={onCompare}
            disabled={disabled}
            block
          >
            Compare &amp; Reconcile
          </Button>
        </Space>
      ) : null}
    </Card>
  );
}
